import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/admin/DateInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ImageUpload from "@/components/admin/ImageUpload";
import api, { apiError, fileUrl } from "@/lib/api";
import { formatDate } from "@/components/public/Shared";

const EMPTY_MEMBER = {
  full_name: "", relationship: "", gender: "", dob: "", phone: "", email: "", whatsapp: "",
  photo_url: "", ministry: "", date_joined: "", baptism_info: "", marriage_info: "",
  baptised: "", communicant: "", notes: "", is_public: false, photo_consent: false,
};

const formatDob = (dob) => {
  if (!dob) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dob));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(dob);
};

const RELATIONSHIPS = ["Head of Family", "Wife / Spouse", "Husband / Spouse", "Mother", "Father", "Grandmother", "Grandfather", "Sister", "Brother", "Daughter", "Son", "Daughter-in-law", "Son-in-law", "Granddaughter", "Grandson", "Other"];

export default function FamilyDetail() {
  const { familyId } = useParams();
  const [family, setFamily] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/families/${familyId}`)
      .then((r) => setFamily(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [familyId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY_MEMBER); setEditing("new"); };
  const openEdit = (m) => {
    const init = {};
    Object.keys(EMPTY_MEMBER).forEach((k) => { init[k] = m[k] ?? EMPTY_MEMBER[k]; });
    setForm(init);
    setEditing(m);
  };

  const save = async () => {
    if (!form.full_name.trim()) {
      toast.error("Member name is required");
      return;
    }
    if (!form.relationship) {
      toast.error("Relationship to Head of Family is required");
      return;
    }
    if (!form.baptised || !form.communicant) {
      toast.error("Baptised and Communicant Member are required (Yes / No)");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post(`/admin/families/${familyId}/members`, form);
        toast.success("Member added");
      } else {
        await api.put(`/admin/members/${editing.member_id}`, form);
        toast.success("Member updated");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/members/${deleting.member_id}`);
      toast.success("Member deleted");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!family) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div data-testid="family-detail" className="space-y-6">
      <Button variant="ghost" asChild className="text-muted-foreground" data-testid="family-back-btn">
        <Link to="/admin/families"><ArrowLeft className="mr-2 h-4 w-4" /> Back to Family Directory</Link>
      </Button>

      <div className="rounded-lg border border-border bg-white p-6 shadow-sm sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            {family.photo_url ? (
              <img src={fileUrl(family.photo_url)} alt={`${family.head_of_family} family`} className="h-16 w-16 rounded-full object-cover ring-2 ring-gold/40" />
            ) : (
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary text-navy/40">
                <UserRound className="h-7 w-7" />
              </span>
            )}
            <div>
              <p className="font-mono text-xs font-semibold text-gold-dark">{family.family_id}</p>
              <h1 className="font-heading text-2xl font-bold text-navy" data-testid="family-detail-name">{family.head_of_family}</h1>
              <p className="text-sm text-muted-foreground">
                {family.is_public ? "Public on website" : "Private"} {family.date_joined ? `· Joined ${formatDate(family.date_joined)}` : ""}
              </p>
            </div>
          </div>
        </div>
        <div className="mt-6 grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Head of Family</p><p className="mt-1 font-medium text-navy">{family.head_of_family || "—"}</p></div>
          <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Phone</p><p className="mt-1 font-medium text-navy">{family.phone || "—"}</p></div>
          <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Email</p><p className="mt-1 font-medium text-navy">{family.email || "—"}</p></div>
          <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Address</p><p className="mt-1 font-medium text-navy">{family.address || "—"}</p></div>
        </div>
        {family.notes && (
          <p className="mt-4 rounded-md bg-secondary/60 px-4 py-3 text-xs text-muted-foreground">Private notes: {family.notes}</p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-bold text-navy">Family Members ({family.members.length})</h2>
        <Button onClick={openCreate} className="bg-navy text-cream hover:bg-navy-light" data-testid="member-add-btn">
          <Plus className="mr-2 h-4 w-4" /> Add Member
        </Button>
      </div>

      {family.members.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-white/60 px-8 py-12 text-center" data-testid="members-empty">
          <p className="font-heading text-lg font-bold text-navy">No members yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Add the first member of this family.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-white shadow-sm">
          <table className="w-full text-sm" data-testid="members-table">
            <thead>
              <tr className="border-b border-border bg-secondary/60 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Name</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Relationship</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Gender</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Phone</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">DOB</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Visibility</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-navy/70">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {family.members.map((m) => (
                <tr key={m.member_id} className="hover:bg-secondary/30" data-testid={`member-row-${m.member_id}`}>
                  <td className="px-4 py-3 font-medium text-navy">{m.full_name}</td>
                  <td className="px-4 py-3 text-foreground/80">{m.relationship || "—"}</td>
                  <td className="px-4 py-3 text-foreground/80">{m.gender || "—"}</td>
                  <td className="px-4 py-3 text-foreground/80">{m.phone || "—"}</td>
                  <td className="px-4 py-3 text-foreground/80">{formatDob(m.dob)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.is_public ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground"}`}>
                      {m.is_public ? "Public" : "Private"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(m)} data-testid={`member-edit-${m.member_id}`} aria-label="Edit member">
                        <Pencil className="h-4 w-4 text-navy/60" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleting(m)} data-testid={`member-delete-${m.member_id}`} aria-label="Delete member">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="member-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-navy">{editing === "new" ? "Add Member" : "Edit Member"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="mf-name">Full Name <span className="text-destructive">*</span></Label>
              <Input id="mf-name" data-testid="member-field-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Relationship to Head of Family <span className="text-destructive">*</span></Label>
                <Select value={form.relationship || undefined} onValueChange={(v) => setForm({ ...form, relationship: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="member-field-relationship"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {(form.relationship && !RELATIONSHIPS.includes(form.relationship) ? [...RELATIONSHIPS, form.relationship] : RELATIONSHIPS).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Gender</Label>
                <Select value={form.gender || undefined} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="member-field-gender"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mf-dob">Date of Birth</Label>
                <DateInput id="mf-dob" data-testid="member-field-dob" value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="mf-joined">Date Joined</Label>
                <DateInput id="mf-joined" data-testid="member-field-joined" value={form.date_joined} onChange={(v) => setForm({ ...form, date_joined: v })} className="mt-1.5" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Baptised <span className="text-destructive">*</span></Label>
                <Select value={form.baptised || undefined} onValueChange={(v) => setForm({ ...form, baptised: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="member-field-baptised"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes" data-testid="member-baptised-yes">Yes</SelectItem>
                    <SelectItem value="no" data-testid="member-baptised-no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Communicant Member <span className="text-destructive">*</span></Label>
                <Select value={form.communicant || undefined} onValueChange={(v) => setForm({ ...form, communicant: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="member-field-communicant"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mf-phone">Phone</Label>
                <Input id="mf-phone" data-testid="member-field-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="mf-email">Email</Label>
                <Input id="mf-email" data-testid="member-field-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label htmlFor="mf-ministry">Ministry</Label>
              <Input id="mf-ministry" data-testid="member-field-ministry" value={form.ministry} onChange={(e) => setForm({ ...form, ministry: e.target.value })} className="mt-1.5" placeholder="e.g. Choir, Sunday School" />
            </div>
            <div>
              <Label htmlFor="mf-whatsapp">WhatsApp / Mobile Number</Label>
              <Input id="mf-whatsapp" data-testid="member-field-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="mt-1.5" placeholder="e.g. +91 98765 43210" />
            </div>
            <div>
              <Label htmlFor="mf-baptism">Baptism Information</Label>
              <Input id="mf-baptism" data-testid="member-field-baptism" value={form.baptism_info} onChange={(e) => setForm({ ...form, baptism_info: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="mf-marriage">Marriage Information (if applicable)</Label>
              <Input id="mf-marriage" data-testid="member-field-marriage" value={form.marriage_info} onChange={(e) => setForm({ ...form, marriage_info: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>Photograph</Label>
              <div className="mt-1.5">
                <ImageUpload value={form.photo_url} onChange={(v) => setForm({ ...form, photo_url: v })} testId="member-field-photo" />
              </div>
            </div>
            <div>
              <Label htmlFor="mf-notes">Notes (private)</Label>
              <Textarea id="mf-notes" data-testid="member-field-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" />
            </div>
            <div className="rounded-md border border-gold/40 bg-gold/10 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox id="mf-public" data-testid="member-field-public" checked={form.is_public} onCheckedChange={(v) => setForm({ ...form, is_public: !!v })} />
                <Label htmlFor="mf-public" className="text-sm font-normal">Show this member on the public website</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="mf-consent" data-testid="member-field-consent" checked={form.photo_consent} onCheckedChange={(v) => setForm({ ...form, photo_consent: !!v })} />
                <Label htmlFor="mf-consent" className="text-sm font-normal">Consent given to show photograph publicly</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="member-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-navy text-cream hover:bg-navy-light" data-testid="member-form-save">
              {saving ? "Saving…" : "Save Member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent data-testid="member-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete member?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove “{deleting?.full_name}” from family {familyId}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="member-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="member-delete-confirm">
              Delete Member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
